import React from 'react';

import Chart from 'react-apexcharts';
import { ICampaign, IDevice } from '../interfaces';
import { generateDateBlocksForCampaigns, getFreeSpaceFromTimeConflicts } from '../utils/campaign-viewer';
import { SIGN_COLORS } from '../utils/constants';
import { generateDeviceOccupation, getHoursSumFromPlanning, getUsedSpace } from '../utils/weeks-viewer';

export const ChartScreensUsageSigns = (props: {
    campaigns: ICampaign[];
    devices: {
        [key: number]: IDevice;
    };
    signs: { [key: string]: number };
    weekStart: Date;
}) => {
    const data: {
        [key_sign_name: string]: {
            [key_camp_name: string]: { percentage: number | null; devices: number; hours: number };
        };
    } = {};

    const signKeysSorted = Object.keys(props.signs).sort(function (a, b) {
        return props.signs[b] - props.signs[a];
    });

    // First, get total list of signs used for every campaigns
    for (const sign of signKeysSorted) {
        if (!(sign in data)) {
            data[sign] = {};
        }
    }

    // Second, populate values
    for (const c of props.campaigns) {
        const hoursSum = getHoursSumFromPlanning(c.planning);

        // Iterate over every sign
        for (const s of Object.keys(data)) {
            if (s in c.signs) {
                const usedSpaceRatio = getUsedSpace([c], props.signs[s], props.weekStart, c.signs[s]);

                data[s][c.name] = {
                    percentage: Math.round(10000 * usedSpaceRatio * c.slots) / 100,
                    devices: c.signs[s],
                    hours: hoursSum
                };
            } else {
                data[s][c.name] = { percentage: null, devices: 0, hours: hoursSum };
            }
        }
    }

    const series_data = [];

    for (const c of props.campaigns) {
        const dataset = [];
        // For each sign
        for (const s of Object.keys(data)) {
            dataset.push(data[s][c.name].percentage);
        }

        series_data.push({
            name: c.name,
            data: dataset
        });
    }

    let data_available: number[] = new Array(Object.keys(data).length).fill(100);
    for (const d of series_data) {
        for (let i = 0; i < d.data.length; i++) {
            const num = d.data[i];

            if (num === null) {
                continue;
            }

            data_available[i] -= num;
        }
    }

    series_data.push({
        name: 'Libre',
        data: data_available.map((d) => {
            if (d === 0) return null;
            return Math.round(100 * d) / 100;
        })
    });

    return (
        <Chart
            height="400"
            type="bar"
            series={series_data.map(({ name, data }) => ({ name, data }))}
            options={{
                chart: {
                    type: 'bar',
                    stacked: true,
                    height: 350,
                    zoom: {
                        enabled: false
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: true
                    }
                },
                stroke: {
                    width: 1,
                    colors: ['#fff']
                },
                title: { text: 'Répartition / enseigne' },
                xaxis: {
                    categories: Object.keys(data),
                    labels: {
                        formatter: (val) => `${val}%`
                    },
                    max: 100,
                    min: 0
                },
                tooltip: {
                    y: {
                        formatter: (val, opts) => {
                            const signName = Object.keys(data)[opts.dataPointIndex];
                            const campName = Object.keys(data[signName])[opts.seriesIndex];

                            if (!campName) {
                                return `${val}%`;
                            }

                            const data_tt = data[signName][campName];

                            return `${val}% / ${data_tt.devices} écr. / ${data_tt.hours}h`;
                        }
                    }
                },
                fill: {
                    opacity: [...props.campaigns.map(() => 0.4), 1]
                },
                legend: {
                    position: 'top',
                    horizontalAlign: 'left',
                    offsetX: 40
                }
            }}
        />
    );
};

export const ChartSlotsAvailable = (props: {
    campaigns: ICampaign[];
    devices: {
        [key: number]: IDevice;
    };
    signs: { [key: string]: number };
    planning: number[][];
    startDate: Date;
}) => {
    const data = generateDeviceOccupation(
        props.campaigns,
        Object.keys(props.devices).map((d) => parseInt(d)),
        props.planning,
        props.startDate
    );

    const device_slots_taken: {
        [key: number]: IDevice[];
    } = {
        0: [],
        1: [],
        2: [],
        3: [],
        4: [],
        5: []
    };

    for (const d of Object.keys(data)) {
        const dev = props.devices[parseInt(d)];
        if (!dev) {
            continue;
        }

        const slots_taken = Math.min(5, data[parseInt(d)]);

        device_slots_taken[slots_taken].push(dev);
    }

    const dataset = Object.keys(props.signs).map((name) => ({
        name: name
            .replace('CARREFOUR', 'C.')
            .replace('INTERMARCHE', 'ITM.')
            .replace('O MARCHE FRAIS', 'OMF')
            .replace('COCCINELLE EXPRESS', 'COCC. EXPR.'),
        data: new Array(6).fill(0),
        color: SIGN_COLORS[name] || '#000'
    }));

    for (let i = 0; i <= 5; i++) {
        const devices = device_slots_taken[i];

        for (const d of devices) {
            if (!(d as any).Shop || !(d as any).Shop.Sign) {
                continue;
            }

            const sIndex = Object.keys(props.signs).findIndex((name) => name === (d as any).Shop.Sign.name);

            if (sIndex === -1) {
                continue;
            }

            dataset[sIndex].data[i]++;
        }
    }

    for (const signDataset of dataset) {
        signDataset.data.reverse();

        for (let i = 5; i > 0; i--) {
            signDataset.data[i - 1] += signDataset.data[i];
        }
    }

    const totals = [0, 0, 0, 0, 0];
    for (let i = 1; i <= 5; i++) {
        for (const ds of dataset) {
            totals[i - 1] += ds.data[i];
        }
    }

    return (
        <Chart
            height="550"
            type="bar"
            series={dataset}
            options={{
                chart: {
                    type: 'bar',
                    stacked: true,
                    height: 350,
                    zoom: {
                        enabled: false
                    }
                },
                annotations: {
                    points: new Array(5).fill(0).map((_, i) => ({
                        seriesIndex: 0,
                        x: i + 1,
                        label: {
                            borderColor: '#775DD0',
                            offsetY: 0,
                            style: {
                                color: '#fff',
                                background: '#775DD0'
                            },
                            text: `${totals[i]} écr.`
                        }
                    }))
                },
                plotOptions: {
                    bar: {
                        borderRadius: 8,
                        horizontal: false
                    }
                },

                stroke: {
                    width: 1,
                    colors: ['#fff']
                },
                title: { text: 'Slots disponibles / enseigne' },
                xaxis: {
                    type: 'numeric',
                    categories: [0, 1, 2, 3, 4, 5],
                    labels: {
                        formatter: (val) => `${val} slot(s)`
                    },
                    min: 1,
                    max: 5,
                    offsetX: 1,
                    floating: false,
                    tickAmount: 4
                },
                tooltip: {
                    y: {
                        formatter: (val) => {
                            return `${val} écr.`;
                        }
                    }
                },
                fill: {
                    opacity: 1
                },
                legend: {
                    position: 'top',
                    horizontalAlign: 'left',
                    offsetX: 40
                }
            }}
        />
    );
};

export const ChartConflicts = (props: {
    campaigns: ICampaign[];
    data: {
        [key: string]: number[][];
    };
    currentCampaignBlock: {
        start: Date;
        end: Date;
        devicesOverflowing: number[];
    }[];
}) => {
    const series: any[] = [];

    for (const space of props.currentCampaignBlock) {
        const serie = {
            x: 'Nouvelle campagne',
            y: [space.start.getTime(), space.end.getTime()],
            fillColor: '#00e396',
            strokeColor: '#00e396'
        };

        if (space.devicesOverflowing.length > 0) {
            serie.fillColor = serie.strokeColor = '#FF4560';
        }

        series.push(serie);
    }

    const maxCampaignIndex = series.length;

    for (const k in props.data) {
        for (const dates of props.data[k]) {
            const serie: any = {
                x: k,
                y: dates
            };

            if (k.startsWith('⏸')) {
                serie.fillColor = '#f1c205';
                serie.strokeColor = '#f1c205';
            } else if (k.startsWith('🖊')) {
                serie.fillColor = '#775DD0';
                serie.strokeColor = '#775DD0';
            }

            series.push(serie);
        }
    }

    return (
        <Chart
            height="650"
            type="rangeBar"
            series={[
                {
                    name: 'Campagnes',
                    data: series
                }
            ]}
            options={{
                title: { text: 'Timeline campagne et conflits' },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '80%'
                    }
                },
                chart: {
                    id: `conflicts-chart-${Math.random()}`,
                    toolbar: {
                        show: true,
                        tools: {
                            download: false,
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            pan: false,
                            reset: false
                        }
                    }
                },
                tooltip: {
                    x: {
                        format: 'dddd dd/MM HH:mm'
                    },
                    y: {
                        formatter: (val, opts) => {
                            if (
                                opts &&
                                opts.dataPointIndex < maxCampaignIndex &&
                                props.currentCampaignBlock[opts.dataPointIndex].devicesOverflowing.length > 0
                            ) {
                                return `${val} (${
                                    props.currentCampaignBlock[opts.dataPointIndex].devicesOverflowing.length
                                } écr. conflit)`;
                            }

                            return `${val}`;
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: (_, opts) => {
                        if (
                            opts.dataPointIndex >= maxCampaignIndex ||
                            props.currentCampaignBlock[opts.dataPointIndex].devicesOverflowing.length === 0
                        ) {
                            return '';
                        }

                        return `${props.currentCampaignBlock[opts.dataPointIndex].devicesOverflowing.length} écr.`;
                    },
                    style: {
                        colors: ['#f3f4f5', '#fff']
                    }
                },
                xaxis: {
                    type: 'datetime',
                    labels: {
                        datetimeUTC: false
                    },
                    min: props.campaigns[0].startDate.getTime(),
                    max: props.campaigns[0].endDate.getTime()
                },
                stroke: {
                    width: 1,
                    show: true,
                    lineCap: 'butt'
                },
                fill: {
                    type: 'solid',
                    opacity: 0.6
                },
                colors: ['#008ffb'],
                legend: {
                    position: 'top',
                    horizontalAlign: 'left'
                }
            }}
        />
    );
};

export const ChartFreeSpace = (props: {
    campaigns: ICampaign[];
    start: Date;
    end: Date;
    devices: number[];
    height?: string;
}) => {
    const time_conflicts: {
        start: Date;
        end: Date;
        campaign: ICampaign;
    }[] = [];

    const data = generateDateBlocksForCampaigns(
        props.campaigns.sort((a, b) => a.name.localeCompare(b.name)),
        props.start,
        props.end,
        time_conflicts
    );

    // Sort time conflicts
    time_conflicts.sort((a, b) => a.start.getTime() - b.start.getTime());

    const freeSpace = getFreeSpaceFromTimeConflicts(
        time_conflicts,
        new Date(props.start),
        new Date(props.end),
        props.devices
    );

    const series: any[] = [];

    for (const k in data) {
        for (const dates of data[k]) {
            const serie: any = {
                x: k,
                y: dates
            };

            if (k.startsWith('⏸')) {
                serie.fillColor = '#f1c205';
                serie.strokeColor = '#f1c205';
            } else if (k.startsWith('🖊')) {
                serie.fillColor = '#775DD0';
                serie.strokeColor = '#775DD0';
            }

            series.push(serie);
        }
    }

    const freeStartIndex = series.length;

    for (const space of freeSpace) {
        series.push({
            x: 'Espace libre',
            y: [space.start.getTime(), space.end.getTime()],
            fillColor: '#00E396',
            strokeColor: '#00E396'
        });
    }

    return (
        <Chart
            height={`${props.height || 350}`}
            type="rangeBar"
            series={[
                {
                    name: 'Campagnes',
                    data: series
                }
            ]}
            options={{
                title: { text: 'Planning de la semaine' },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '80%'
                    }
                },
                chart: {
                    toolbar: {
                        show: true,
                        tools: {
                            download: false,
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            pan: false,
                            reset: false
                        }
                    }
                },
                tooltip: {
                    x: {
                        format: 'dddd dd/MM HH:mm'
                    },
                    y: {
                        formatter: (val, opts) => {
                            if (opts && opts.dataPointIndex >= freeStartIndex) {
                                return `${val} ${
                                    freeSpace[opts.dataPointIndex - freeStartIndex].devicesAvailableCount
                                } écr.`;
                            }

                            return `${val}`;
                        }
                    }
                },
                xaxis: {
                    type: 'datetime',
                    labels: {
                        datetimeUTC: false
                    },
                    min: props.start.getTime(),
                    max: props.end.getTime()
                },
                stroke: {
                    width: 1,
                    show: true,
                    lineCap: 'butt'
                },
                dataLabels: {
                    enabled: true,
                    formatter: (_, opts) => {
                        if (opts.dataPointIndex < freeStartIndex) {
                            return '';
                        }

                        return `${freeSpace[opts.dataPointIndex - freeStartIndex].devicesAvailableCount} écr.`;
                    },
                    style: {
                        colors: ['#f3f4f5', '#fff']
                    }
                },
                fill: {
                    type: 'solid',
                    opacity: 0.6
                },
                legend: {
                    position: 'top',
                    horizontalAlign: 'left'
                }
            }}
        />
    );
};
